import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { listCuisines, listTags } from "@/data/taxonomy";
import { errMsg } from "@/lib/errors";
import { labelsForSlugs } from "@/lib/taxonomyLabels";
import type { Lang } from "@/i18n/translations";
import type { Cuisine, Tag } from "@/types/taxonomy";
import { isShowcasePath } from "@/services/showcase";

/**
 * Loads the cuisine + tag vocabulary once for the whole app and exposes
 * slug→label resolvers, so every consumer surface (card, detail, offers) can
 * render localized chips without re-fetching or prop-drilling the lookup.
 *
 * Failures are swallowed to empty lists — a venue then simply shows no chips
 * rather than the feed crashing.
 */

interface TaxonomyContextValue {
  cuisines: Cuisine[];
  tags: Tag[];
  /** Localized cuisine labels for a venue's `cuisine_slugs`, in lookup order. */
  cuisineLabels: (slugs: string[] | undefined, lang: Lang) => string[];
  /** Localized tag labels for a venue's `tag_slugs`, in lookup order. */
  tagLabels: (slugs: string[] | undefined, lang: Lang) => string[];
  /** Append an operator-created entry so it's selectable app-wide immediately. */
  addCuisine: (row: Cuisine) => void;
  addTag: (row: Tag) => void;
}

const TaxonomyContext = createContext<TaxonomyContextValue | null>(null);

export function TaxonomyProvider({
  children,
  initial,
}: {
  children: ReactNode;
  /** A bundled vocabulary (the `/showcase-cluj` demo); skips the fetch. */
  initial?: { cuisines: Cuisine[]; tags: Tag[] };
}) {
  const [cuisines, setCuisines] = useState<Cuisine[]>(initial?.cuisines ?? []);
  const [tags, setTags] = useState<Tag[]>(initial?.tags ?? []);

  useEffect(() => {
    // The demo nests its own provider with a bundled vocabulary; the app-wide
    // one must not reach Supabase on a page loaded there either.
    if (initial || isShowcasePath()) return;
    let cancelled = false;
    // Failures degrade to no chips (progressive enhancement) — but leave a
    // trace so a broken vocabulary isn't invisible while debugging.
    void listCuisines().then((c) => !cancelled && setCuisines(c)).catch((e) => console.warn("cuisines load failed:", errMsg(e, "unknown error")));
    void listTags().then((t) => !cancelled && setTags(t)).catch((e) => console.warn("tags load failed:", errMsg(e, "unknown error")));
    return () => {
      cancelled = true;
    };
    // `initial` is fixed for the provider's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<TaxonomyContextValue>(
    () => ({
      cuisines,
      tags,
      cuisineLabels: (slugs, lang) => labelsForSlugs(slugs, cuisines, lang),
      tagLabels: (slugs, lang) => labelsForSlugs(slugs, tags, lang),
      addCuisine: (row) =>
        setCuisines((prev) => (prev.some((c) => c.slug === row.slug) ? prev : [...prev, row])),
      addTag: (row) => setTags((prev) => (prev.some((t) => t.slug === row.slug) ? prev : [...prev, row])),
    }),
    [cuisines, tags],
  );

  return <TaxonomyContext.Provider value={value}>{children}</TaxonomyContext.Provider>;
}

export function useTaxonomy(): TaxonomyContextValue {
  const ctx = useContext(TaxonomyContext);
  if (!ctx) throw new Error("useTaxonomy must be used inside <TaxonomyProvider>");
  return ctx;
}
